// Show 10 groups
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Pagination } from "antd";
import moment from "moment";
import { useAuth } from "../../contexts/auth-context";
import "./styles/TransactionListDisplay.css";

const TransactionListDisplay = (props) => {
    const { transactions } = props;
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [groupedTransactionsData, setGroupedTransactionsData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10; // Show 10 grouped transactions per page

    const navigate = useNavigate();
    const auth = useAuth();
    const loginEmail = auth.currentUser?.email;

    // GK 2026-04-24 Gets the amount_user_etnered if there it was a split payment, or the txn_amount if single payment
    const getTransactionAmount = (transaction) => {
        if (transaction.total_amount_user_entered) {
            return Number(transaction.total_amount_user_entered).toFixed(2);
        }

        if (transaction.txn_amount) {
            return Number(transaction.txn_amount).toFixed(2);
        }

        return '';
    }

    useEffect(() => {
        function handleOnline() {
            setIsOnline(true);
        }

        function handleOffline() {
            setIsOnline(false);
        }

        window.addEventListener("online", handleOnline);
        window.addEventListener("offline", handleOffline);

        return () => {
            window.removeEventListener("online", handleOnline);
            window.removeEventListener("offline", handleOffline);
        };
    }, []);


    // GK 2026-04-22 Groups transactions tgoether by Date
    useEffect(() => {
        const groupedTransactions = (transactions || []).reduce((prevResult, transaction) => {
            const date = moment(transaction.created_at).format("ll");
            const transactionAmount2 = transaction.txn_amount ? Number(transaction.txn_amount).toFixed(2) : "";
            const transactionAmount = getTransactionAmount(transaction);
            const recipientField =
                loginEmail === transaction.recipient_login_email
                    ? transaction.sender_login_email
                    : transaction.recipient_login_email;

            const transactionType =
                transaction.recipient_login_email.toLowerCase() === loginEmail.toLowerCase()
                    ? "recipient"
                    : "sender";
            const transactionPrefix = transactionType === "recipient" ? "+$" : "$";

            const formattedTransaction = {
                transactionType,
                key: transaction._id,
                recipient: recipientField,
                memo: transaction.memo_at_txn,
                transactionDate: date,
                amount: transactionPrefix + transactionAmount,
                transactionFee: transaction.blockchain_fees,
                status: transaction.txn_status,
                transactionId: transaction.exchange_txn_id,
                splitPaymentStatus: transaction.split_payment_status,
                transactionFeeStatus: transaction.transactionFee?.txn_status
            };

            if (prevResult[formattedTransaction.transactionDate]) {
                prevResult[formattedTransaction.transactionDate].push(formattedTransaction);
            } else {
                prevResult[formattedTransaction.transactionDate] = [formattedTransaction];
            }

            return prevResult;
        }, {});

        setGroupedTransactionsData(Object.entries(groupedTransactions));
    }, [transactions]);

    // Paginate grouped transactions (by date)
    const totalGroups = groupedTransactionsData.length;
    const paginatedGroups = groupedTransactionsData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    const getTransactionClass = (transaction) => {
    //    if (transaction.status === "waiting_for_clearing" || transaction.status === "pending") {
    //       return "transaction-pending";
    //     } else if (transaction.status === "failed") {
        //   return "transaction-failed";
        // }  
        if (transaction.transactionType === "recipient") {
          return "transaction-recipient";
        }
        return "";
      };

    const getSplitPaymentClass = (transaction) => {
        // If is a split payment but one of the transactions hasn't been paid at all
        if (transaction.splitPaymentStatus === 'pending') {
            return 'transaction-split-payment-pending';
        }

        // If the 2 payments have been made but one or both of the transactions haven't been confirmed by the excahnge
        if(
            transaction.status &&
            transaction.transactionFeeStatus && 
            (
                !['sent', 'completed'].includes(transaction.status.toLowerCase()) ||
                !['sent', 'completed'].includes(transaction.transactionFeeStatus.toLowerCase())
            )
        ) {
            return 'transaction-split-payment-unconfirmed';
        }
        
        return '';
    }
    const getTransactionClassForText = (transaction) => {
        if (transaction.splitPaymentStatus === 'pending') {
            return 'transaction-split-payment-pending'
        } else if (transaction.status === "waiting_for_clearing") {
            return "transaction-waiting-for-clearing";
        } else if (transaction.status === "pending") {
            return "transaction-pending"
        } else if (transaction.status === "failed") {
            return "transaction-failed";
        } else if (transaction.transactionType === "recipient") {
            return "transaction-recipient";
        }
        return "";
    };

    const getTransactionClassForBackground = (transaction) => {
        if ( 
                (
                    transaction.status === "waiting_for_clearing" || 
                    transaction.transactionFeeStatus === "waiting_for_clearing" ||
                    transaction.status === "waiting_for_signature" || 
                    transaction.transactionFeeStatus === "waiting_for_signature"
                )
                && transaction.transactionType === "sender" 
            ) { return "transaction-waiting-for-clearing-sender-background"; } 
        else if ( 
            transaction.splitPaymentStatus === 'pending' && transaction.transactionType === "sender" 
        ) { return "transaction-split-payment-pending-background"; }
        else if (
            (transaction.status === "pending" || transaction.transactionFeeStatus === "pending") && transaction.transactionType === "sender"
        ) { return "transaction-pending-sender-background"}
        else if (
            (
                transaction.status === "waiting_for_clearing" || 
                transaction.transactionFeeStatus === "waiting_for_clearing" ||
                transaction.status === "waiting_for_signature" || 
                transaction.transactionFeeStatus === "waiting_for_signature" || 
                transaction.splitPaymentStatus === 'pending' || 
                transaction.transactionType === "sender" ||
                transaction.status === "pending" || 
                transaction.transactionFeeStatus === "pending"
            ) && transaction.transactionType === "recipient" 
        ) { return "transaction-pending-recipient-background" }
        else if (
            (
                transaction.status === "failed" || 
                transaction.transactionFeeStatus === "failed" || 
                transaction.status === "canceled" ||
                transaction.transactionFeeStatus === "canceled" ||
                transaction.status === "expired" || 
                transaction.transactionFeeStatus === "expired" 
            )
        ) { return "transaction-failed" }
        else if (transaction.transactionType === "recipient") {
            return "transaction-recipient";
        }
        return "";
    };

    return (
        <div>
            {isOnline ? (
                <Card className="transaction-card-body">
                    {paginatedGroups.map(([groupedTransKey, transactions]) => (
                        <div className="transaction-group" key={groupedTransKey}>
                            <p className="transaction-date">{groupedTransKey}</p>
                            {transactions.map((transaction) => (
                                <Card
                                    key={transaction.key}
                                    className={`transaction-card-item ${getTransactionClassForBackground(transaction)}`}
                                    type="inner"
                                    onClick={() => navigate(`/transactions/${transaction.transactionId}`)}
                                >
                                    <div
                                        className={`transaction-item-details ${getTransactionClass(transaction)}`}
                                    >
                                        <div>
                                            <div style={{ fontSize: "16px" }}>{transaction.recipient}</div>
                                            <div className="transaction-memo">
                                                <i style={{ fontSize: "13px" }}>{transaction.memo}</i>
                                            </div>
                                        </div>
                                        <div>
                                            <div className="transaction-amount">{transaction.amount}</div>
                                            { transaction.splitPaymentStatus === 'pending' && <div><i style={{fontSize: '13px'}}>Incomplete</i></div> }
                                            {/* This should be a big if/else statement here */}
                                        </div>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    ))}
                    {/* Pagination Component */}
                    <Pagination
                        current={currentPage}
                        pageSize={pageSize}
                        total={totalGroups}
                        onChange={(page) => setCurrentPage(page)}
                        style={{ textAlign: "center", marginTop: "20px" }}
                    />
                </Card>
            ) : (
                <p style={{ color: "red", display: "flex", justifyContent: "center", alignItems: "center" }}>
                    You are offline. Please check your connection.
                </p>
            )}
        </div>
    );
};

export default TransactionListDisplay;

// Show all
// import React, { useEffect, useState } from "react";
// import { useNavigate } from "react-router-dom";
// import { Card } from 'antd';
// import moment from 'moment';

// import { useAuth } from "../../contexts/auth-context";
// import './styles/TransactionListDisplay.css';
// const gridStyle = { width: '100%'};


// const TransactionListDisplay = (props) => {
//     const { transactions, inProgress } = props;
//     const [isOnline, setIsOnline] = useState(navigator.onLine);
    
//     const navigate = useNavigate();
//     const [data, setData] = useState([]);
//     const [groupedTransactionsData, setGroupedTransactionsData] = useState([]);
//     const auth = useAuth();
//     const loginEmail = auth.currentUser?.email;

//     useEffect(() => {
//         function handleOnline() {
//           setIsOnline(true);
//         }
    
//         function handleOffline() {
//           setIsOnline(false);
//         }
    
//         window.addEventListener('online', handleOnline);
//         window.addEventListener('offline', handleOffline);
    
//         // Clean up event listeners on component unmount
//         return () => {
//           window.removeEventListener('online', handleOnline);
//           window.removeEventListener('offline', handleOffline);
//         };
//       }, []);

//     useEffect(() => {
//         const rows = (transactions || []).map((transaction) => {
//           const date = moment(transaction.created_at).format('ll');
//           const transactionAmount = transaction.txn_amount ? Number(transaction.txn_amount).toFixed(2) : "";
//           const recipientField = loginEmail === transaction.recipient_login_email ? transaction.sender_login_email : transaction.recipient_login_email;
//           return {
//             key: transaction._id,
//             recipient: recipientField,
//             memo: transaction.memo_at_txn,
//             transactionDate: `${date}`,
//             amount: transactionAmount,
//             transactionFee: transaction.blockchain_fees,
//             status: transaction.txn_status,
//             transactionId: transaction.exchange_txn_id
//           }
//         });

//         // "Grouping" the transactions makes it so all transactions made in a single day appear under one date on the table
//         const groupedTransactions = (transactions || []).reduce((prevResult, transaction) => {
//             const date = moment(transaction.created_at).format('ll');
//             const transactionAmount = transaction.txn_amount ? Number(transaction.txn_amount).toFixed(2) : '';
//             const recipientField = loginEmail === transaction.recipient_login_email ? transaction.sender_login_email : transaction.recipient_login_email;

//             const transactionType = transaction.recipient_login_email.toLowerCase() === loginEmail.toLowerCase() ? 'recipient' : 'sender';
//             const transactionPrefix = transactionType === 'recipient' ? '+$' : '$' ;
//             // console.log('transactionType ===>', transactionType);
          
//             // console.log('loginEmail ==>', loginEmail, 'Transaction =>', transaction);

//             const formattedTransaction = {
//                 transactionType,
//                 key: transaction._id,
//                 recipient: recipientField,
//                 memo: transaction.memo_at_txn,
//                 transactionDate: `${date}`,
//                 amount: transactionPrefix + transactionAmount,
//                 transactionFee: transaction.blockchain_fees,
//                 status: transaction.txn_status,
//                 transactionId: transaction.exchange_txn_id,
//             }

//             if(prevResult[formattedTransaction.transactionDate]) {
//                 prevResult[formattedTransaction.transactionDate].push(formattedTransaction);
//             } else {
//                 prevResult[formattedTransaction.transactionDate] = [formattedTransaction];
//             }

//             return prevResult;
            
//         }, {});
        
//         setGroupedTransactionsData(groupedTransactions);

//         setData(rows);
//       }, [transactions]);

//     return (
//         <div>
//         {isOnline ? (
//             <Card className="transaction-card-body">
//                 {
//                     Object.keys(groupedTransactionsData).map((groupedTransKey) => (
//                         <div className="transaction-group" key={groupedTransKey}>
//                             <p className="transaction-date">{groupedTransKey}</p>
//                             {
//                             groupedTransactionsData[groupedTransKey].map(transaction => (
//                                     <Card 
//                                         key={transaction.key} 
//                                         className="transaction-card-item" 
//                                         type="inner" 
//                                         onClick={() => navigate(`/transactions/${transaction.transactionId}`)}
//                                     >
//                                         <div 
//                                             className={`transaction-item-details ${transaction.transactionType === 'recipient' ? 'transaction-recipient' : ''}`}
//                                         >
//                                             <div>
//                                                 <div style={{fontSize: '16px'}}>
//                                                     {transaction.recipient}
//                                                 </div>
//                                                 <div className="transaction-memo">
//                                                     <i style={{fontSize: '13px'}}>{transaction.memo}</i>
//                                                 </div>
//                                             </div>
//                                             <div className="transaction-amount">{transaction.amount}</div>
//                                         </div>
//                                     </Card>
//                             )) 
//                             }
//                         </div>
//                     ))
//                 }
//                 {/* {
//                     data.map(transaction => (
//                         <div key={transaction.key}>
//                             <p className="transaction-date">{transaction.transactionDate}</p>
//                             <Card className="transaction-card-item" type="inner" onClick={handleClick}>
//                                 {transaction.recipient}
//                             </Card>
//                         </div>
//                     ))
//                 } */}
//             </Card>
//             ) : (
//                 <p style={{ color: 'red', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>You are offline. Please check your connection.</p>
//             )}
//         </div>
//       );
// }
// export default TransactionListDisplay;